import { DashboardConfigurationDependencies, Granularity } from 'src/app/dashboards/shared/dashboard-config/dashboard-config.model';
import { DashboardDefinition } from 'src/app/dashboards/shared/analyses/_shared/dashboard-definition.model';
import { DASHBOARD_REGISTRY } from 'src/app/dashboards/shared/analyses/_shared/dashboard-registry';
import {
  COMPARISON_FOUR_GRID_SHELL_ID,
} from 'src/app/dashboards/shared/analyses/comparison/_shared/dashboard-comparison-four-grid/dashboard-comparison-four-grid-shell.profile';
import { DashboardComparisonFourGridDefinition } from 'src/app/dashboards/shared/analyses/comparison/_shared/dashboard-comparison-four-grid/dashboard-comparison-four-grid.definition';
import { COMPARISON_INDUSTRIES_SHELL_ID } from 'src/app/dashboards/shared/analyses/comparison/_shared/dashboard-comparison-industries/dashboard-comparison-industries-shell.profile';
import {
  DOSSIER_DEVELOPMENT_SHELL_ID,
  DOSSIER_MARKET_SHELL_ID,
  DOSSIER_ONION_SHELL_ID,
  DOSSIER_OVERVIEW_SHELL_ID,
} from 'src/app/dashboards/shared/analyses/dossiers/_shared/dossier-shell.profile';
import { DossierStatesDefinition } from 'src/app/dashboards/shared/analyses/dossiers/_shared/dossier-states.definition';
import { resolveDossierHeadingLabels } from 'src/app/dashboards/shared/analyses/dossiers/_shared/dossier-metric.profile';
import { FORECAST_COMPARISON_SHELL_ID } from 'src/app/dashboards/shared/analyses/forecasts/_shared/dashboard-forecast-comparison/dashboard-forecast-comparison-shell.profile';
import { FORECAST_STRUCTURE_SHELL_ID } from 'src/app/dashboards/shared/analyses/forecasts/_shared/dashboard-forecast-structure/dashboard-forecast-structure-shell.profile';
import { DashboardForecastStructureDefinition } from 'src/app/dashboards/shared/analyses/forecasts/_shared/dashboard-forecast-structure/dashboard-forecast-structure.definition';
import { INDEX_RANKING_DEVELOPMENT_SHELL_ID } from 'src/app/dashboards/shared/analyses/index/_shared/dashboard-index-ranking-development/dashboard-index-ranking-development-shell.profile';
import { RANKING_SHELL_ID } from 'src/app/dashboards/shared/analyses/rankings/_shared/dashboard-ranking/dashboard-ranking-shell.profile';
import { DashboardRankingDefinition } from 'src/app/dashboards/shared/analyses/rankings/_shared/dashboard-ranking/dashboard-ranking.definition';
import { RANKING_MULTI_COLUMN_SHELL_ID } from 'src/app/dashboards/shared/analyses/rankings/_shared/dashboard-ranking-multi-column/dashboard-ranking-multi-column-shell.profile';
import { TIME_SERIES_BAR_SHELL_ID } from 'src/app/dashboards/shared/analyses/time-series/_shared/dashboard-time-series-bar/dashboard-time-series-bar-shell.profile';
import { DataSlice, SlicePreviewSeries } from 'src/app/state/library/library.model';
import {
  resolveSliceMetricFilterLabel,
  resolveSlicePreviewGranularity,
  SLICE_SANKEY_SOURCE_CODE,
} from 'src/app/state/library/library.util';
import { SliceDashboardConfig } from '../dashboard-configs/slice-dashboard-config.model';
import { COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID, getComparisonDuelDefinition } from 'src/app/dashboards/shared/analyses/comparison/employed/db4x5x4-comparison-employed-districts/db4x5x4-comparsion-employed-districts.definition';
import { INDEX_RANKING_SHELL_ID } from 'src/app/dashboards/shared/analyses/index/_shared/dashboard-index-ranking/dashboard-index-ranking-shell.profile';

/** Preview chart type for a library card. */
export type SlicePreviewChart =
  | 'ranking'
  | 'bar'
  | 'sankey'
  | 'onion'
  | 'line'
  | 'wzrank'
  | 'quad-line'
  | 'duel'
  | 'forecast-line'
  | 'forecast-stack'
  | 'slope';

/**
 * Shell package ids the library can live-refresh / preview.
 * Closed set; each value comes from the shell package const.
 */
export type SliceShellId =
  | typeof TIME_SERIES_BAR_SHELL_ID
  | typeof RANKING_SHELL_ID
  | typeof RANKING_MULTI_COLUMN_SHELL_ID
  | typeof DOSSIER_OVERVIEW_SHELL_ID
  | typeof DOSSIER_ONION_SHELL_ID
  | typeof DOSSIER_MARKET_SHELL_ID
  | typeof DOSSIER_DEVELOPMENT_SHELL_ID
  | typeof COMPARISON_FOUR_GRID_SHELL_ID
  | typeof COMPARISON_INDUSTRIES_SHELL_ID
  | typeof COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID
  | typeof FORECAST_COMPARISON_SHELL_ID
  | typeof FORECAST_STRUCTURE_SHELL_ID
  | typeof INDEX_RANKING_SHELL_ID
  | typeof INDEX_RANKING_DEVELOPMENT_SHELL_ID;

export type SliceDefinition = Pick<
  DashboardDefinition<unknown>,
  | 'shellId'
  | 'filters'
  | 'preFilterTransformers'
  | 'postFilterTransformers'
  | 'chartTransformers'
  | 'view'
>;

type SliceDefinitionGetter = (dashboardId: string) => SliceDefinition | undefined;

/**
 * The definition a slice of this shell refers to. The variant tells analysis variants sharing an id
 * apart (forecast Bundesvergleich Teilmärkte / WZ-Klassen).
 */
function resolveShellDefinition(
  entry: SliceShellRegistryEntry,
  shellId: string,
  dashboardId: string,
  variant?: string,
): SliceDefinition | undefined {
  return entry.getDefinition
    ? entry.getDefinition(dashboardId)
    : (DASHBOARD_REGISTRY.get(dashboardId, { shellId, variant: variant || undefined }) as SliceDefinition | undefined);
}

/** Args passed to a dynamic registry `previewTitle` resolver. */
export type SlicePreviewTitleInput = {
  dashboardId?: string;
  definition?: SliceDefinition;
  filterValues?: DataSlice['filterValues'];
};

/**
 * Preview builders keyed in SlicePreviewBuildersService.
 * `generic` is the category × time reducer used by bar / ranking shells.
 */
export type SlicePreviewBuilderId =
  | 'generic'
  | 'sankey'
  | 'onion'
  | 'line'
  | 'wzrank'
  | 'quad-line'
  | 'duel'
  | 'forecast-line'
  | 'forecast-stack'
  | 'slope';

export type SliceShellRegistryEntry = {
  previewChart: SlicePreviewChart;
  /** Legacy shells outside `DASHBOARD_REGISTRY`; registered shells resolve through it. */
  getDefinition?: SliceDefinitionGetter;
  previewBuilder: SlicePreviewBuilderId;
  /**
   * Card title lead for this shell’s preview.
   * A string or a function when the lead depends on the product definition
   * (e.g. Länder- vs Teilmärkte-Ranking).
   */
  previewTitle?: string | ((input: SlicePreviewTitleInput) => string);
  /**
   * Card subtitle template for this shell’s preview chart.
   * - `kind` + `structure`: `Sankey · Deutschland → Bundesländer · 2024`
   * - `dimensions`: `16 Bundesländer × 7 WZ-Klassen · 2024`
   * - four-grid: resolved dynamically (`Unternehmen · 16 Bundesländer · 2021–2024`)
   * Time is always appended from filter year/years when present.
   */
  previewSub?: {
    kind?: string;
    structure?: string;
    /** Multiselect counts joined with × (matrix / grid shells). */
    dimensions?: Array<{
      filterKey: string;
      singular: string;
      plural: string;
      /** Values omitted from the count (e.g. Σ Germany `-1`). */
      excludeValues?: string[];
    }>;
  };
  /**
   * Filter names to disable for headless live refresh.
   * Four-grid uses this so the per-quadrant select stays off while chart data
   * covers all saved `comparison_grids`.
   */
  initiallyDisabledFilters?: (definition: SliceDefinition) => string[];
};

function resolveDossierMetricPreviewTitle({
  definition,
  filterValues,
}: SlicePreviewTitleInput): string {
  const metricLabel = resolveSliceMetricFilterLabel({
    filterValues: filterValues ?? {},
  });
  if (metricLabel) {
    return metricLabel;
  }
  const labels = (definition as DossierStatesDefinition<unknown> | undefined)
    ?.headingLabels;
  return labels?.standaloneEntityLabel ?? labels?.entityAfterDerLabel ?? '';
}

/** Index ranking+development card title lead: selected index metric (e.g. Umsätze pro Unternehmen). */
function resolveIndexMetricPreviewTitle({
  filterValues,
}: SlicePreviewTitleInput): string {
  return resolveSliceMetricFilterLabel({
    filterValues: filterValues ?? {},
  });
}

/**
 * Single registration surface for library slices: card chart, definition
 * lookup, and preview builder.
 */
export const SLICE_SHELL_REGISTRY: Record<SliceShellId, SliceShellRegistryEntry> = {
  [TIME_SERIES_BAR_SHELL_ID]: {
    previewChart: 'bar',
    previewBuilder: 'generic',
    previewTitle: 'Entwicklung',
  },
  [RANKING_SHELL_ID]: {
    previewChart: 'ranking',
    previewBuilder: 'generic',
    previewTitle: ({ definition }) => {
      switch ((definition as DashboardRankingDefinition)?.view.granularityId) {
        case Granularity.States: return 'Länder-Ranking';
        case Granularity.SubMarkets: return 'Teilmärkte-Ranking';
        case Granularity.Districts: return 'Kreise-Ranking';
        default: return 'Ranking';
      }
    },
  },
  [RANKING_MULTI_COLUMN_SHELL_ID]: {
    previewChart: 'wzrank',
    previewBuilder: 'wzrank',
    previewTitle: 'WZ-Klassen-Ranking',
    previewSub: {
      dimensions: [
        { filterKey: 'states', singular: 'Bundesland', plural: 'Bundesländer' },
        { filterKey: 'wz_classes', singular: 'WZ-Klasse', plural: 'WZ-Klassen' },
      ],
    },
  },
  [DOSSIER_OVERVIEW_SHELL_ID]: {
    previewChart: 'sankey',
    previewBuilder: 'sankey',
    previewTitle: ({ definition, filterValues }) => {
      const dossier = definition as DossierStatesDefinition<unknown> | undefined;
      if (!dossier) {
        return '';
      }
      const typeLabel = resolveSliceMetricFilterLabel({ filterValues: filterValues ?? {} });
      return resolveDossierHeadingLabels(dossier, typeLabel).structureLabel;
    },
    previewSub: {
      kind: 'Sankey',
      structure: 'Deutschland → Bundesländer',
    },
  },
  [DOSSIER_ONION_SHELL_ID]: {
    previewChart: 'onion',
    previewBuilder: 'onion',
    previewTitle: 'Struktur-Querschnitt',
    previewSub: {
      kind: 'Zwiebel',
      structure: '4 Ebenen',
    },
  },
  [DOSSIER_MARKET_SHELL_ID]: {
    previewChart: 'ranking',
    previewBuilder: 'generic',
    previewTitle: resolveDossierMetricPreviewTitle,
  },
  [DOSSIER_DEVELOPMENT_SHELL_ID]: {
    previewChart: 'line',
    previewBuilder: 'line',
    previewTitle: resolveDossierMetricPreviewTitle,
  },
  [COMPARISON_FOUR_GRID_SHELL_ID]: {
    previewChart: 'quad-line',
    previewBuilder: 'quad-line',
    previewTitle: ({ definition }) => {
      switch ((definition as DashboardComparisonFourGridDefinition)?.view?.uiSettings?.metric) {
        case 'sub_market': return '4-Teilmärkte-Vergleich';
        case 'state': return '4-Länder-Vergleich';
        default: return '';
      }
    },
    initiallyDisabledFilters: (definition) => {
      const metric = (definition as DashboardComparisonFourGridDefinition).view
        ?.uiSettings?.metric;
      return metric ? [metric] : [];
    },
  },
  [COMPARISON_INDUSTRIES_SHELL_ID]: {
    previewChart: 'bar',
    previewBuilder: 'generic',
  },
  [COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID]: {
    previewChart: 'duel',
    getDefinition: getComparisonDuelDefinition,
    previewBuilder: 'duel',
    previewTitle: 'Vergleichsanalyse',
    previewSub: {
      structure: 'Beschäftigtenstruktur',
    },
  },
  [FORECAST_COMPARISON_SHELL_ID]: {
    previewChart: 'forecast-line',
    previewBuilder: 'forecast-line',
    previewTitle: 'Prognose',
  },
  [FORECAST_STRUCTURE_SHELL_ID]: {
    previewChart: 'forecast-stack',
    previewBuilder: 'forecast-stack',
    previewTitle: 'Prognose gestapelt',
  },
  [INDEX_RANKING_SHELL_ID]: {
    previewChart: 'ranking',
    previewBuilder: 'generic',
    previewTitle: resolveIndexMetricPreviewTitle,
  },
  [INDEX_RANKING_DEVELOPMENT_SHELL_ID]: {
    previewChart: 'slope',
    previewBuilder: 'slope',
    previewTitle: resolveIndexMetricPreviewTitle
  },
};

const DEFAULT_SLICE_PREVIEW_CHART: SlicePreviewChart = 'bar';

function isSliceShellId(id: string): id is SliceShellId {
  return Object.prototype.hasOwnProperty.call(SLICE_SHELL_REGISTRY, id);
}

/** Type-safe registry lookup for a saved / untyped shell id. */
export function getSliceShellEntry(
  shellId: string | undefined,
): SliceShellRegistryEntry | undefined {
  if (!shellId || !isSliceShellId(shellId)) {
    return undefined;
  }
  return SLICE_SHELL_REGISTRY[shellId];
}

/** Card mini-chart for a slice, keyed by stamped shellId. */
export function resolveSlicePreviewChart(slice: DataSlice): SlicePreviewChart {
  return getSliceShellEntry(slice.shellId)?.previewChart ?? DEFAULT_SLICE_PREVIEW_CHART;
}

/** Minimal fields needed to resolve a shell-owned preview subtitle. */
export type SlicePreviewSubInput = Pick<
  DataSlice,
  'shellId' | 'dashboardId' | 'filterValues'
> &
  Partial<Pick<DataSlice, 'previewSeries'>>;

export interface SlicePreviewLabelResolvers {
  subMarket?: (id: string | number) => string | undefined;
}

/**
 * Four-grid sub: `Unternehmen · 16 Bundesländer · 2021–2024`
 * Dimension follows the table axis (states / Teilmärkte / WZ), not the 4 compared grids.
 */
function resolveComparisonFourGridPreviewSub(slice: SlicePreviewSubInput): string {
  const granularity = resolveSlicePreviewGranularity(slice.dashboardId, slice.shellId);
  const dimensions: NonNullable<SliceShellRegistryEntry['previewSub']>['dimensions'] =
    granularity === Granularity.States
      ? [
        {
          filterKey: 'states',
          singular: 'Bundesland',
          plural: 'Bundesländer',
          excludeValues: ['-1'],
        },
      ]
      : granularity === Granularity.SubMarkets
        ? [
          {
            filterKey: 'sub_markets',
            singular: 'Teilmarkt',
            plural: 'Teilmärkte',
          },
        ]
        : granularity === Granularity.WzClasses
          ? [
            {
              filterKey: 'wz_classes',
              singular: 'WZ-Klasse',
              plural: 'WZ-Klassen',
            },
          ]
          : [];

  const kind = resolveSliceMetricFilterLabel(slice);

  return formatSlicePreviewSub({ kind, dimensions }, slice.filterValues ?? {});
}

/**
 * Duel sub: `Design · Beschäftigtenstruktur · 2024`
 * (Teilmarkt single-select · compared structure · period).
 */
function resolveComparisonDuelPreviewSub(
  slice: SlicePreviewSubInput,
  resolvers: SlicePreviewLabelResolvers,
): string {
  const registered = SLICE_SHELL_REGISTRY[COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID].previewSub;
  const rawMarket = slice.filterValues?.['sub_market'];
  const market =
    rawMarket == null || rawMarket === ''
      ? ''
      : resolvers.subMarket?.(rawMarket as string | number) ?? '';
  const structure = registered?.structure ?? '';

  return formatSlicePreviewSub({ kind: market, structure }, slice.filterValues ?? {});
}

const SANKEY_NODE_SUB_LABEL: Record<string, { source: string; target: string }> = {
  country: { source: 'Deutschland', target: 'Länder' },
  state: { source: 'Bundesland', target: 'Bundesländer' },
  industry_sub_market: { source: 'Branche', target: 'Branchen' },
  sub_market: { source: 'Teilmarkt', target: 'Teilmärkte' },
  wz: { source: 'WZ-Klasse', target: 'WZ-Klassen' },
};

function formatSankeyPreviewStructure(sourceType: string, targetType: string): string {
  const source = SANKEY_NODE_SUB_LABEL[sourceType]?.source ?? sourceType;
  const target = SANKEY_NODE_SUB_LABEL[targetType]?.target ?? targetType;
  return `${source} → ${target}`;
}

/** Onion sub: `Zwiebel · 2 Ebenen · 2023` from the captured level count. */
function resolveDossierOnionPreviewSub(slice: SlicePreviewSubInput): string {
  const previewSub = SLICE_SHELL_REGISTRY[DOSSIER_ONION_SHELL_ID].previewSub;
  const levelCount = slice.previewSeries?.length ?? 0;
  const structure =
    levelCount > 0
      ? `${levelCount} ${levelCount === 1 ? 'Ebene' : 'Ebenen'}`
      : previewSub?.structure ?? '';

  return formatSlicePreviewSub({ kind: previewSub?.kind, structure }, slice.filterValues ?? {});
}

/** Sankey sub: `Sankey · Branche → Teilmärkte · 2023` from the captured edge. */
function resolveDossierSankeyPreviewSub(slice: SlicePreviewSubInput): string {
  const previewSub = SLICE_SHELL_REGISTRY[DOSSIER_OVERVIEW_SHELL_ID].previewSub;
  const sourceMeta = slice.previewSeries?.find((s) => s.code === SLICE_SANKEY_SOURCE_CODE);
  const [sourceType, targetType] = (sourceMeta?.shortName ?? '').split('|');
  const structure =
    sourceType && targetType
      ? formatSankeyPreviewStructure(sourceType, targetType)
      : previewSub?.structure ?? '';

  return formatSlicePreviewSub({ kind: previewSub?.kind, structure }, slice.filterValues ?? {});
}

/** Forecast marker appended to any forecast-bearing time range. */
const FORECAST_YEAR_MARKER = '(**)';

function formatTimeRange(first: number | string, last: number | string): string {
  return `${first}` === `${last}` ? `${first}` : `${first}–${last}`;
}

/** Time label for card subtitles, from year / period filter values. */
function resolveFilterTimeLabel(
  filterValues: Record<string, unknown> | Map<string, unknown>,
): string {
  const get = (key: string): unknown =>
    filterValues instanceof Map ? filterValues.get(key) : filterValues[key];

  const years = get('years') ?? get('years_line_chart');
  const periods = get('periods');
  const year = get('year');
  const periodValue = get('period_value');

  if (Array.isArray(years) && years.length >= 1) {
    return formatTimeRange(years[0], years[years.length - 1]);
  }
  if (Array.isArray(periods) && periods.length >= 1) {
    return formatTimeRange(periods[0], periods[periods.length - 1]);
  }
  if (year != null && year !== '') {
    return `${year}`;
  }
  if (periodValue != null && periodValue !== '') {
    return `${periodValue}`;
  }
  return '';
}

/**
 * True when at least one preview point carries a real CI band.
 * Requires `hi > lo`: slices saved before blank CI cells were parsed strictly
 * hold degenerate `0/0` pairs, which are not a band.
 */
function hasConfidenceBand(series: SlicePreviewSeries[] | undefined): boolean {
  return (series ?? []).some((entry) => {
    const los = entry.lo ?? [];
    const his = entry.hi ?? [];
    return los.some((lo, index) => {
      const hi = his[index];
      return (
        lo != null &&
        hi != null &&
        Number.isFinite(lo) &&
        Number.isFinite(hi) &&
        hi > lo
      );
    });
  });
}

/**
 * Forecast sub: `Konfidenzintervall · 2018–2027 (**)`.
 * `Konfidenzintervall` appears only when the preview carries a CI band; the
 * range spans preview years (history + forecast), not just the year filter.
 */
function resolveForecastComparisonPreviewSub(slice: SlicePreviewSubInput): string {
  const previewYears = (slice.previewSeries ?? [])
    .flatMap((entry) => entry.years ?? [])
    .filter((year) => Number.isFinite(year));

  const time = previewYears.length
    ? formatTimeRange(Math.min(...previewYears), Math.max(...previewYears))
    : resolveFilterTimeLabel(slice.filterValues ?? {});

  const kind = hasConfidenceBand(slice.previewSeries) ? 'Konfidenzintervall' : '';
  return [kind, time ? `${time} ${FORECAST_YEAR_MARKER}` : '']
    .filter(Boolean)
    .join(' · ');
}

/**
 * Forecast stack sub: `WZ-Klassen · 2020–2027 (**)` or
 * `Teilmärkte · 2020–2027 (**)`, based on the definition's stack dimension.
 */
function resolveForecastStructurePreviewSub(slice: SlicePreviewSubInput): string {
  const previewYears = (slice.previewSeries ?? [])
    .flatMap((entry) => entry.years ?? [])
    .filter((year) => Number.isFinite(year));
  const time = previewYears.length
    ? formatTimeRange(Math.min(...previewYears), Math.max(...previewYears))
    : resolveFilterTimeLabel(slice.filterValues ?? {});

  const legendMapKind = (DASHBOARD_REGISTRY.get(slice.dashboardId, { shellId: FORECAST_STRUCTURE_SHELL_ID }) as
    | DashboardForecastStructureDefinition
    | undefined)?.view?.legendMapKind;
  const dimension =
    legendMapKind === 'wz'
      ? 'WZ-Klassen'
      : legendMapKind === 'submarket'
        ? 'Teilmärkte'
        : '';

  return [dimension, time ? `${time} ${FORECAST_YEAR_MARKER}` : '']
    .filter(Boolean)
    .join(' · ');
}

/**
 * Card title lead from shell registry `previewTitle`
 * (fixed string or definition-aware function).
 */
export function resolveSlicePreviewTitle(args: {
  shellId?: string;
  dashboardId?: string;
  dashboardVariant?: string;
  filterValues?: DataSlice['filterValues'];
}): string {
  const entry = getSliceShellEntry(args.shellId);
  const title = entry?.previewTitle;
  if (!entry || title == null) {
    return '';
  }
  if (typeof title === 'function') {
    return title({
      dashboardId: args.dashboardId,
      definition: args.dashboardId
        ? resolveShellDefinition(entry, args.shellId!, args.dashboardId, args.dashboardVariant)
        : undefined,
      filterValues: args.filterValues,
    });
  }
  return title;
}

/**
 * Format shell-owned preview subtitle.
 * `kind · structure · time` and/or `n Unit × m Unit · time`.
 */
export function formatSlicePreviewSub(
  previewSub: NonNullable<SliceShellRegistryEntry['previewSub']>,
  filterValues: Record<string, unknown> | Map<string, unknown>,
): string {
  const get = (key: string): unknown =>
    filterValues instanceof Map ? filterValues.get(key) : filterValues[key];

  const time = resolveFilterTimeLabel(filterValues);

  let structure = previewSub.structure ?? '';
  if (previewSub.dimensions?.length) {
    structure = previewSub.dimensions
      .map(({ filterKey, singular, plural, excludeValues }) => {
        const raw = get(filterKey);
        if (!Array.isArray(raw)) {
          return '';
        }
        const excluded = new Set((excludeValues ?? []).map(String));
        const count = raw.filter((id) => !excluded.has(`${id}`)).length;
        if (count <= 0) {
          return '';
        }
        return `${count} ${count === 1 ? singular : plural}`;
      })
      .filter(Boolean)
      .join(' × ');
  }

  return [previewSub.kind, structure, time].filter(Boolean).join(' · ');
}

/** Resolve card subtitle from shell registry previewSub, or empty. */
export function resolveSlicePreviewSub(
  slice: SlicePreviewSubInput,
  resolvers: SlicePreviewLabelResolvers = {},
): string {
  if (!slice.shellId) {
    return '';
  }
  if (slice.shellId === COMPARISON_FOUR_GRID_SHELL_ID) {
    return resolveComparisonFourGridPreviewSub(slice);
  }
  if (slice.shellId === FORECAST_COMPARISON_SHELL_ID) {
    return resolveForecastComparisonPreviewSub(slice);
  }
  if (slice.shellId === FORECAST_STRUCTURE_SHELL_ID) {
    return resolveForecastStructurePreviewSub(slice);
  }
  if (slice.shellId === COMPARISON_EMPLOYED_DISTRICTS_SHELL_ID) {
    return resolveComparisonDuelPreviewSub(slice, resolvers);
  }
  if (slice.shellId === DOSSIER_OVERVIEW_SHELL_ID) {
    return resolveDossierSankeyPreviewSub(slice);
  }
  if (slice.shellId === DOSSIER_ONION_SHELL_ID) {
    return resolveDossierOnionPreviewSub(slice);
  }
  const previewSub = getSliceShellEntry(slice.shellId)?.previewSub;
  if (!previewSub) {
    return '';
  }
  return formatSlicePreviewSub(previewSub, slice.filterValues ?? {});
}

/**
 * Look up the product definition for a saved slice via `slice.shellId`.
 * Returns undefined when shellId is missing (legacy), unknown, or no definition matches.
 */
export function getSliceDefinition(slice: DataSlice): SliceDefinition | undefined {
  const entry = getSliceShellEntry(slice.shellId);
  if (!entry) {
    return undefined;
  }

  const definition = resolveShellDefinition(entry, slice.shellId!, slice.dashboardId, slice.dashboardVariant);
  if (!definition) {
    return undefined;
  }
  if (definition.shellId !== slice.shellId) {
    console.warn('[getSliceDefinition] shell id mismatch', {
      dashboardId: slice.dashboardId,
      expected: slice.shellId,
      actual: definition.shellId,
    });
    return undefined;
  }
  return definition;
}

/** Adapt a dashboard definition into headless library wiring. */
export function buildSliceDashboardConfig(
  definition: SliceDefinition,
  deps: DashboardConfigurationDependencies,
): SliceDashboardConfig {
  return {
    filters: definition.filters(deps),
    initiallyDisabledFilters:
      getSliceShellEntry(definition.shellId)?.initiallyDisabledFilters?.(definition) ??
      [],
    preFilterTransformers: definition.preFilterTransformers(deps),
    postFilterTransformers: definition.postFilterTransformers(deps),
    chartTransformers: definition.chartTransformers?.(deps) ?? [],
  };
}
